/* Đánh số chứng từ theo mẫu: {YYYY} {YY} {MM} {DD} {ORG} {####}. Port từ app cũ. */
import { today as todayFn } from './dates';
import { VOUCHER_TYPES, type VoucherType } from './voucher-types';

export type NumReset = 'year' | 'month' | 'never';

export interface NumCfg {
  pattern: string;
  reset: NumReset;
  start: number;
}

export const NUM_DEF: Record<VoucherType, string> = {
  invoice: 'INV-{YYYY}-{####}',
  receipt: 'RC-{YYYY}-{####}',
  debit: 'DN-{YYYY}-{####}',
  credit: 'CN-{YYYY}-{####}',
  payreq: 'DNTT-{YYYY}-{####}',
  cktm: '{####}/{YYYY}-CVCKTM-FLO',
  dep_in: 'TC-{YYYY}-{####}',
  dep_refund: 'HC-{YYYY}-{####}',
  dep_penalty: 'PPC-{YYYY}-{####}',
  pt: 'PTN{YYYY}-{##}',
  pc: 'PCN{YYYY}-{##}',
};

export type NumFormats = Partial<Record<string, Partial<NumCfg>>>;

/** Một đơn vị (công ty) trong hệ thống nhiều đơn vị. `id` = 'main' là đơn vị chính. */
export interface NumCo {
  id: string;
  docOrg?: string;
  short?: string;
  numFmt?: NumFormats;
}

export const MAIN_CO: NumCo = { id: 'main' };

/** Mẫu đánh số của loại `t` theo thiết lập riêng của đơn vị. Thông báo CKTM mặc định mang ký hiệu đơn vị: FLO cho đơn vị chính, các đơn vị khác dùng ký hiệu văn bản / tên viết tắt. */
export const numCfg = (t: VoucherType, co: NumCo = MAIN_CO): NumCfg => ({
  pattern: t === 'cktm' ? `{####}/{YYYY}-CVCKTM-${co.id === 'main' ? 'FLO' : String(co.docOrg || co.short || 'TAS').toUpperCase()}` : NUM_DEF[t] || `${VOUCHER_TYPES[t].pre}-{YYYY}-{####}`,
  reset: 'year',
  start: 1,
  ...((co.numFmt || {})[t] || {}),
});

const reEsc = (x: string): string => x.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export interface NumSpec {
  re: RegExp;
  make: (n: number) => string;
  start: number;
}

export interface NumOpts {
  /** Ký hiệu đơn vị ({ORG}); mặc định lấy từ đơn vị `co`, nếu trống thì FAS như app cũ. */
  org?: string;
  /** Ngày hôm nay (ISO) khi dateIso trống. */
  todayIso?: string;
}

export function numSpec(t: VoucherType, dateIso: string | undefined, cfg?: NumCfg, opts: NumOpts = {}, co: NumCo = MAIN_CO): NumSpec {
  const c = cfg || numCfg(t, co);
  const d = dateIso || opts.todayIso || todayFn();
  const Y = d.slice(0, 4), M = d.slice(5, 7), D = d.slice(8, 10);
  const fixY = c.reset !== 'never', fixM = c.reset === 'month', org = opts.org || co.docOrg || 'FAS';
  let re = '', seqW = 4, hasSeq = false;
  const make: ((n: number) => string)[] = [];
  for (const part of String(c.pattern || '').split(/(\{[A-Z#]+\})/)) {
    if (!part) continue;
    if (/^\{#+\}$/.test(part)) { hasSeq = true; seqW = part.length - 2; re += '(\\d+)'; const w = seqW; make.push(n => String(n).padStart(w, '0')); continue; }
    const tok: Record<string, [string, string]> = {
      '{YYYY}': [Y, fixY ? reEsc(Y) : '\\d{4}'],
      '{YY}': [Y.slice(2), fixY ? reEsc(Y.slice(2)) : '\\d{2}'],
      '{MM}': [M, fixM ? M : '\\d{2}'],
      '{DD}': [D, '\\d{2}'],
      '{ORG}': [org, reEsc(org)],
    };
    const hit = tok[part];
    if (hit) { re += hit[1]; make.push(() => hit[0]); } else { re += reEsc(part); make.push(() => part); }
  }
  if (!hasSeq) { re += '(\\d+)'; make.push(n => String(n).padStart(4, '0')); }
  return { re: new RegExp('^' + re + '$'), make: n => make.map(f => f(n)).join(''), start: Math.max(1, Number(c.start) || 1) };
}

/** Đơn vị của một chứng từ ('main' khi trống). */
export const coOf = (r: { coId?: string | null } | null | undefined): string => (r?.coId && r.coId !== 'main' ? r.coId : 'main');

/**
 * Số kế tiếp của đơn vị `co`, nối tiếp số lớn nhất đã có trong sổ CỦA ĐƠN VỊ ĐÓ
 * (tính cả số của loại khác cùng mẫu để không trùng).
 */
export function nextNo(t: VoucherType, dateIso: string | undefined, register: { no?: string | null; coId?: string | null }[], co: NumCo = MAIN_CO, cfg?: NumCfg, opts: NumOpts = {}): string {
  const sp = numSpec(t, dateIso, cfg, opts, co);
  let max = sp.start - 1;
  for (const r of register) {
    if (coOf(r) !== co.id) continue;
    const m = r.no && String(r.no).match(sp.re);
    if (m) { const n = parseInt(m[1], 10); if (n > max) max = n; }
  }
  return sp.make(max + 1);
}

/** Mã tự tăng dạng tiền tố + số (KH0001, NCC0001, FAS0001…). */
export function nextCode(list: { code?: string | null }[], pre: string, w: number): string {
  let max = 0;
  for (const x of list) {
    if ((x.code || '').startsWith(pre)) { const n = parseInt((x.code as string).slice(pre.length), 10); if (n > max) max = n; }
  }
  return pre + String(max + 1).padStart(w, '0');
}
